// Updated EmailConfig.js with improved layout and alignment
import React, { useState } from 'react';
import { Form, Tabs, Input, Button, Space, Select, Row, Col, Typography, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import EJSTemplateEditor from '../expressionEditor/EJSTemplateEditor';

const { Option } = Select;
const { TabPane } = Tabs;
const { Text } = Typography;

const EmailConfig = () => {
    const [bodyMode, setBodyMode] = useState('html');
    const form = Form.useFormInstance();

    // Single-line EJS templates for compact display
    const singleLineEditorHeight = 36;

    // Set initial data for recipients if empty
    React.useEffect(() => {
        // Initialize with a default "To" recipient if none exists
        const recipients = form.getFieldValue('recipients');
        if (!recipients || recipients.length === 0) {
            form.setFieldsValue({
                recipients: [{ type: 'to', email: '' }]
            });
        }
    }, []);

    return (
        <div className="email-config">
            <Tabs defaultActiveKey="basic">
                <TabPane tab="Basic" key="basic">
                    {/* Subject Field */}
                    <Form.Item
                        name="subject"
                        label="Subject"
                        rules={[{ required: true, message: 'Please enter a subject' }]}
                    >
                        <EJSTemplateEditor
                            multiline={false}
                            height={singleLineEditorHeight}
                            placeholder="Call <%= record.id %> - Score: <%= record.qualScore %>"
                            evaluateOnChange={true}
                            showModeToggle={false}
                            ultraCompact={true}
                        />
                    </Form.Item>

                    {/* Recipients List */}
                    <Form.Item
                        label="Recipients"
                        required
                        className="recipients-container"
                    >
                        <Form.List name="recipients">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }, index) => (
                                        <Row key={key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                                            <Col span={4}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'type']}
                                                    style={{ marginBottom: 0 }}
                                                    initialValue="to"
                                                >
                                                    <Select style={{ width: '100%' }} size="middle">
                                                        <Option value="to">To:</Option>
                                                        <Option value="cc">CC:</Option>
                                                        <Option value="bcc">BCC:</Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={19}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'email']}
                                                    rules={[{ required: true, message: 'Email required' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <EJSTemplateEditor
                                                        multiline={false}
                                                        height={singleLineEditorHeight}
                                                        placeholder="recipient@example.com"
                                                        evaluateOnChange={true}
                                                        showModeToggle={false}
                                                        ultraCompact={true}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={1} style={{
                                                textAlign: 'center',
                                                display: 'flex',
                                                alignItems: 'center',
                                                height: '36px'
                                            }}>
                                                {/* Show delete icon for non-first items, plus icon for last item */}
                                                {index > 0 ? (
                                                    <DeleteOutlined
                                                        onClick={() => remove(name)}
                                                        style={{ color: '#ff4d4f', cursor: 'pointer' }}
                                                    />
                                                ) : null}
                                                {index === fields.length - 1 ? (
                                                    <PlusOutlined
                                                        onClick={() => add()}
                                                        style={{ color: '#1890ff', cursor: 'pointer', marginLeft: 4 }}
                                                    />
                                                ) : null}
                                            </Col>
                                        </Row>
                                    ))}
                                </>
                            )}
                        </Form.List>
                    </Form.Item>

                    {/* Email Body */}
                    <Form.Item
                        name="body"
                        label="Email Body"
                        rules={[{ required: true, message: 'Please enter email body content' }]}
                        style={{ marginTop: 12 }}
                    >
                        <EJSTemplateEditor
                            multiline={true}
                            height={200}
                            mode={bodyMode}
                            onModeChange={setBodyMode}
                            placeholder={bodyMode === 'html' ?
                                "<div>\n  <p>New call evaluation completed:</p>\n  <p>ID: <%= record.id %></p>\n  <p>Score: <%= record.qualScore %></p>\n  <p>Agent: <%= record.agentName %></p>\n</div>" :
                                "New call evaluation completed:\n\nID: <%= record.id %>\nScore: <%= record.qualScore %>\nAgent: <%= record.agentName %>"}
                            evaluateOnChange={true}
                            showModeToggle={true}
                        />
                    </Form.Item>
                </TabPane>

                <TabPane tab="Advanced" key="advanced">
                    {/* Hidden Template Field - moved to advanced */}
                    <Form.Item
                        name="template"
                        label="Email Template"
                        initialValue="default"
                    >
                        <Select>
                            <Option value="default">Default Template</Option>
                            <Option value="custom">Custom Template</Option>
                        </Select>
                    </Form.Item>

                    {/* Custom Headers */}
                    <Form.Item
                        label="Custom Headers"
                        required={false}
                        className="custom-headers-container"
                    >
                        <Form.List name="customHeaders">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }, index) => (
                                        <Row key={key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                                            <Col span={10}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'name']}
                                                    rules={[{ required: true, message: 'Header name required' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <EJSTemplateEditor
                                                        multiline={false}
                                                        height={singleLineEditorHeight}
                                                        placeholder="X-Priority"
                                                        evaluateOnChange={true}
                                                        showModeToggle={false}
                                                        ultraCompact={true}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={13}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'value']}
                                                    rules={[{ required: true, message: 'Header value required' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <EJSTemplateEditor
                                                        multiline={false}
                                                        height={singleLineEditorHeight}
                                                        placeholder="<%= record.qualScore < 70 ? 'High' : 'Normal' %>"
                                                        evaluateOnChange={true}
                                                        showModeToggle={false}
                                                        ultraCompact={true}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={1} style={{
                                                textAlign: 'center',
                                                display: 'flex',
                                                alignItems: 'center',
                                                height: '36px'
                                            }}>
                                                {/* Show delete icon if not the only item */}
                                                {fields.length > 1 ? (
                                                    <DeleteOutlined
                                                        onClick={() => remove(name)}
                                                        style={{ color: '#ff4d4f', cursor: 'pointer' }}
                                                    />
                                                ) : null}
                                            </Col>
                                        </Row>
                                    ))}
                                    {/* Add a plus icon row */}
                                    <Row gutter={8} style={{ marginBottom: 8 }}>
                                        <Col span={23}>
                                            {/* Spacer */}
                                        </Col>
                                        <Col span={1} style={{
                                            textAlign: 'center',
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: '36px',
                                            justifyContent: 'center'
                                        }}>
                                            <PlusOutlined
                                                onClick={() => add()}
                                                style={{ color: '#1890ff', cursor: 'pointer' }}
                                            />
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </Form.List>
                    </Form.Item>

                    {/* Additional Settings */}
                    <Form.Item
                        name="importance"
                        label="Importance"
                    >
                        <Select>
                            <Option value="normal">Normal</Option>
                            <Option value="high">High</Option>
                            <Option value="low">Low</Option>
                        </Select>
                    </Form.Item>
                </TabPane>
            </Tabs>

            {/* Add some CSS for consistent alignment */}
            <style jsx>{`
                .email-config .ant-form-item-label {
                    padding-bottom: 6px;
                }
                .email-config .ant-row {
                    align-items: flex-start;
                }
                .email-config .ant-select-selector {
                    height: 36px !important;
                    display: flex;
                    align-items: center;
                }
            `}</style>
        </div>
    );
};

export default EmailConfig;
